import {
  MAX_HEIGHT_CM,
  MAX_WEIGHT_KG,
  MIN_HEIGHT_CM,
  MIN_WEIGHT_KG,
} from "@/constants";
import { calculateBmi, toNumber } from "@/utils";
import { getStoredQueryParams } from "@/utils/queryParams";

export const HEIGHT_QUERY_PARAM = "height";
export const WEIGHT_QUERY_PARAM = "weight";

export type PrepopulatedPhysicalStats = {
  height?: number;
  weight?: number;
  bmi?: number;
};

/**
 * Parses a physical stat from a query param value. Accepts both "." and ","
 * as decimal separator. Returns undefined when the value is missing, not a
 * number, or outside the allowed range.
 */
export const parsePhysicalStat = (
  value: string | undefined,
  min: number,
  max: number,
): number | undefined => {
  if (value === undefined || value.trim() === "") {
    return;
  }

  const parsed = toNumber(value.trim());

  if (!Number.isFinite(parsed) || parsed < min || parsed > max) {
    return;
  }

  return parsed;
};

/**
 * Reads `height` (cm) and `weight` (kg) from the captured query params and
 * returns the values that should be used to prepopulate the onboarding state.
 * BMI is only calculated when both values are present and valid.
 */
export const getPrepopulatedPhysicalStats = (): PrepopulatedPhysicalStats => {
  const params = getStoredQueryParams([HEIGHT_QUERY_PARAM, WEIGHT_QUERY_PARAM]);

  const height = parsePhysicalStat(
    params[HEIGHT_QUERY_PARAM],
    MIN_HEIGHT_CM,
    MAX_HEIGHT_CM,
  );
  const weight = parsePhysicalStat(
    params[WEIGHT_QUERY_PARAM],
    MIN_WEIGHT_KG,
    MAX_WEIGHT_KG,
  );

  const stats: PrepopulatedPhysicalStats = {};

  if (height !== undefined) {
    stats.height = height;
  }
  if (weight !== undefined) {
    stats.weight = weight;
  }
  if (height !== undefined && weight !== undefined) {
    stats.bmi = calculateBmi(height, weight);
  }

  return stats;
};
